import { Injectable } from '@angular/core';
import {
  Map,
  MapWithState,
  Layer,
  LayerState,
  Folder,
  FolderState,
  LayerDocumentsSet,
  MapSettings,
  FolderVisibility,
  LayerTypes,
  SectionType
} from 'src/app/modules/cop/store/cop/models';
import { LayerDataChangeEvent, LayerDataChangeType } from 'src/app/modules/cop/store/cop/models/layer-data-change-event.model';
import { produce } from 'immer';
import { Subject } from 'rxjs';
import { Document, DocumentsSet } from '../../core/store/models/document.model';
import { Dataset } from '../../core/store/models/dataset.model';
import { DatasetSubtype } from 'src/app/modules/core/store/models/dataset.model';
import { MapLayerQueryBuildService } from './data/map-layer-query-build.service';
import { MapDataWorkerService } from '../web-workers/client/map-data-worker.service';
import { NotificationService } from 'src/app/modules/notifications/store/notification.service';
import {
  Notification,
  NotificationTranslation,
  NotificationType,
  NotificationStatus
} from 'src/app/modules/notifications/store/notifications.models';
import { v4 } from 'uuid';
import { UserContextService } from '../../core/services/user-context.service';
import { AlertProviderIcons } from 'src/app/modules/cop/store/cop/models/alert-provider-icons.model';
import { DataFormattingService } from 'src/app/modules/cop/services/format/data-formatting.service';
import { FieldDataType } from 'src/app/modules/core/store/models/document.model';
import { HorizonRatingsUtilsService } from '../components/horizon-ratings/horizon-ratings-utils';
import { AlertStatusService } from './rsd/alert-status-service';

@Injectable({
  providedIn: 'root'
})
export class MapStateService {
  public layerDataChangeEvents$: Subject<LayerDataChangeEvent> = new Subject();

  constructor(
    private mapDataWorkerService: MapDataWorkerService,
    private notificationService: NotificationService,
    private userContextService: UserContextService,
    private dataFormattingService: DataFormattingService,
    private horizonRatingsUtilsService: HorizonRatingsUtilsService,
    private alertStatusService: AlertStatusService
  ) {}

  ngOnDestroy(): void {
    this.layerDataChangeEvents$.complete();
    this.layerDataChangeEvents$ = null;
  }

  /**
   * Inits the MapWithState, flattening out the structure so that it
   * is setup for searching and binding to controls.
   * @param mapToProcess The map for which state should be initialized.
   * @returns Initialized MapWithState
   */
  initMapStateFromMap(mapToProcess: Map, mapSettings: MapSettings, isDuplicating?: boolean): MapWithState {
    const folders = this.getAllFoldersFromFolders(mapToProcess.folders);

    if (folders.some(f => f.sectionType === SectionType.Alerts)) {
      this.alertStatusService.loadAlertRatingScale();
    }

    const intelRatingsSection = folders.find(f => f.sectionType === SectionType.HorizonLocationIntelRatings);
    if (!!intelRatingsSection) {
      this.horizonRatingsUtilsService.initialize(intelRatingsSection);
    }

    const layers = this.getAllLayersFromFolders(mapToProcess.folders);
    const layersSet = {};
    const layerStates = {};
    layers
      .filter(l => l.isActive === true)
      .forEach(layer => {
        layersSet[layer.uId] = layer;
        // Weather Imagery layers are not visible be default!
        let isVisible = false;
        if (layer.type === LayerTypes.WeatherImagery) {
          isVisible = false;
          if (!!mapSettings.layerVisibility && mapSettings.layerVisibility[layer.uId] === true) {
            isVisible = true;
          }
        } else {
          isVisible = !!!mapSettings.layerVisibility || !(mapSettings.layerVisibility[layer.uId] === false);
        }
        layerStates[layer.uId] = {
          documentCount: 0,
          isVisible: isVisible,
          isLoading: isDuplicating ? false : layer.type !== LayerTypes.WeatherImagery,
          documents: {}
        };
      });

    const foldersSet = {};
    const folderStates = {};
    folders.forEach(folder => {
      foldersSet[folder.uId] = folder;
      folderStates[folder.uId] = {
        documentCount: 0,
        visibility:
          !!!mapSettings.folderVisibility || !(mapSettings.folderVisibility[folder.uId] === FolderVisibility.hidden)
            ? FolderVisibility.visible
            : FolderVisibility.hidden,
        expanded: false,
        isLoading: !isDuplicating
      };
    });

    return {
      map: mapToProcess,
      foldersSet,
      layers,
      layersSet,
      documentsSet: {},
      folderStates,
      layerStates,
      workflowStatusVisibility: mapSettings.workflowStatusVisibilityByLayerTypeAndName,
      sectionWidgetUserSettings: mapSettings.sectionWidgetUserSettings,
      hiddenDocumentsByLayer: mapSettings.hiddenDocumentsByLayer,
      datasetsSet: {},
      selectedDocuments: {},
      geoFieldsForOptionalDisplay: mapSettings.geoFieldsForOptionalDisplay?.filter(s => !!!(s as any).stepName)
    };
  }

  setMapStateToLoading(mapState: MapWithState): MapWithState {
    Object.values(mapState.layersSet)
      .filter(l => l.isActive === true)
      .forEach(l => {
        if (!!mapState.layerStates[l.uId]) {
          mapState.layerStates[l.uId].isLoading = l?.type !== LayerTypes.WeatherImagery && !!mapState.datasetsSet[l?.uId];
          mapState.layerStates[l.uId].documentCount = 0;
        }
      });

    return mapState;
  }

  canLayerHaveLoading(mapState: MapWithState, layerUId: string): boolean {
    if (!!!mapState?.layersSet || !!!mapState?.datasetsSet) {
      return false;
    }
    const layer = mapState?.layersSet[layerUId];

    if (!layer) {
      return false;
    }

    const dataset = mapState?.datasetsSet[layer?.datasetUId];

    if (!dataset) {
      return false;
    }

    return layer?.type !== LayerTypes.WeatherImagery && dataset?.subtype !== DatasetSubtype.TropicalCyclone;
  }

  updateMapStateSetsFromMap(mapState: MapWithState): MapWithState {
    const layers = this.getAllLayersFromFolders(mapState.map.folders);
    mapState.layers = layers;
    mapState.layersSet = {};
    layers
      .filter(l => l.isActive === true)
      .forEach(layer => {
        mapState.layersSet[layer.uId] = layer;
      });
    layers.forEach(l => {
      if (!!!mapState.layerStates[l.uId]) {
        mapState.layerStates[l.uId] = {
          documentCount: 0,
          isVisible: l.type !== LayerTypes.WeatherImagery,
          isLoading: false,
          documents: {}
        };
      }
    });
    let keys = Object.keys(mapState.layerStates);
    keys.forEach(k => {
      if (!mapState.layers.find(l => l.uId === k)) {
        delete mapState.layerStates[k];
        delete mapState.layersSet[k];
      }
    });

    const folders = this.getAllFoldersFromFolders(mapState.map.folders);
    mapState.foldersSet = {};
    folders.forEach(folder => {
      mapState.foldersSet[folder.uId] = folder;
    });
    folders.forEach(f => {
      if (!!!mapState.folderStates[f.uId]) {
        mapState.folderStates[f.uId] = {
          documentCount: 0,
          visibility: FolderVisibility.visible,
          expanded: false,
          isLoading: true
        };
      }
    });
    keys = Object.keys(mapState.folderStates);
    keys.forEach(k => {
      if (!folders.find(f => f.uId === k)) {
        delete mapState.folderStates[k];
        delete mapState.foldersSet[k];
      }
    });

    return mapState;
  }

  addMapStateLayerDocuments(mapState: MapWithState, layerUId: string, documents: Document[], totalDocumentCount?: number) {
    if (!!!mapState.layerStates[layerUId]) {
      mapState.layerStates[layerUId] = {};
    }
    if (!!!mapState.layerStates[layerUId]?.documents) {
      mapState.layerStates[layerUId].documents = {};
    }
    documents.forEach(addDocument => {
      const document = {
        ...addDocument
      };

      // add the layer uid to the document so that it can be used
      // for quick layer lookup if needed
      document.__layerUId = layerUId;

      // add the document to the full documents set. this is useful
      // for finding a document be its uid
      mapState.documentsSet[addDocument.uid] = document;

      // add the document to the layer list of docs. its just a pointer
      // so little cost but is useful for knowing which layers reference which
      // documents
      mapState.layerStates[layerUId].documents[addDocument.uid] = document;
    });

    this.calculateDocumentCountsForLayerAndFolders(mapState, layerUId, totalDocumentCount);
  }

  updateMapStateLayerDocuments(mapState: MapWithState, layerUId: string, documents: Document[], totalDocumentCount?: number) {
    let layerState = mapState.layerStates[layerUId];

    if (!!layerState) {
      documents.forEach(document => {
        const updatedDocUId = document.uid;
        mapState.documentsSet[updatedDocUId] = document;
        if (!!!layerState.documents) {
          layerState.documents = {};
        }
        layerState.documents[updatedDocUId] = document;
      });
    }

    this.calculateDocumentCountsForLayerAndFolders(mapState, layerUId, totalDocumentCount);
  }

  deleteMapStateLayerDocuments(mapState: MapWithState, layerUId: string, documentUIds: string[]) {
    const layerState = mapState.layerStates[layerUId];

    if (!!layerState) {
      documentUIds.forEach(documentUid => {
        if (!!mapState.documentsSet[documentUid]) {
          delete mapState.documentsSet[documentUid];
        }
        if (!!layerState.documents[documentUid]) {
          delete layerState.documents[documentUid];
        }
      });
    }
    this.calculateDocumentCountsForLayerAndFolders(mapState, layerUId);
  }

  getAllLayersFromFolders(folders: Folder[]): Layer[] {
    const layers: Layer[] = [];

    folders.map(folder => {
      layers.push(...folder.layers);

      if (folder.folders?.length > 0) {
        const childLayers = this.getAllLayersFromFolders(folder.folders);
        layers.push(...childLayers);
      }
    });

    return layers;
  }

  getAllFoldersFromFolders(folders: Folder[]): Folder[] {
    const foundFolders: Folder[] = [];

    folders?.map(folder => {
      foundFolders.push(folder);

      if (folder.folders?.length > 0) {
        const childFolders = this.getAllFoldersFromFolders(folder.folders);
        foundFolders.push(...childFolders);
      }
    });

    return foundFolders;
  }

  addLayerDocumentsForEvent(
    currentMapState: MapWithState,
    changeEvent: LayerDataChangeEvent,
    skipNotifications: boolean = false
  ): MapWithState {
    const { layerUId, changes, totalDocumentCount } = changeEvent;

    const mapWithState = produce(currentMapState, draft => {
      this.addMapStateLayerDocuments(draft, layerUId, Object.values(changes), totalDocumentCount);
    });

    this.layerDataChangeEvents$.next(changeEvent);

    // this sends the in-app notifications for new documents. the "send" process will check
    // to ensure the "notifications" are active before sending events
    if (!skipNotifications) {
      this.sendNotificationsForEvent(mapWithState, layerUId, Object.values(changeEvent.changes ?? {}));
    }

    return mapWithState;
  }

  updateLayerDocumentsForEvent(currentMapState: MapWithState, changeEvent: LayerDataChangeEvent): MapWithState {
    const { layerUId, changes, totalDocumentCount } = changeEvent;

    let mapWithState = currentMapState;

    if (!currentMapState.layersSet || !currentMapState.datasetsSet) {
      return mapWithState;
    }

    const layer = currentMapState.layersSet[layerUId];
    const dataset = currentMapState.datasetsSet[layer?.datasetUId];

    // we need to remove documents that are not part of the update if they are getting bbox query
    const removeIfNotUpdated = MapLayerQueryBuildService.isUsingBoundingBoxUpdateQuery(layer, dataset);

    if (removeIfNotUpdated == true) {
      const documentUIdsToUpdate = Object.keys(changes);
      const layerState = mapWithState.layerStates[layerUId];
      if (!!layerState) {
        const changeEvent = {
          type: LayerDataChangeType.Remove,
          layerUId: layerUId,
          changes: {}
        } as LayerDataChangeEvent;

        Object.values(layerState.documents).forEach(d => {
          if (!documentUIdsToUpdate.includes(d.uid)) {
            changeEvent.changes[d.uid] = d;
          }
        });
        if (Object.keys(changeEvent.changes).length > 0) {
          mapWithState = this.removeLayerDocumentsForEvent(mapWithState, changeEvent);
        }
      }
    }

    mapWithState = produce(mapWithState, draft => {
      this.updateMapStateLayerDocuments(draft, layerUId, Object.values(changes), totalDocumentCount);
    });

    this.layerDataChangeEvents$.next(changeEvent);

    // TL - turning this off for now until we decide if we want to send events based on workflow status
    // this sends the in-app notifications for updated documents. the "send" process will check
    // to ensure the "notifications" are active before sending events
    // this.sendNotificationsForEvent(mapWithState, layerUId, Object.values(changeEvent.changes ?? {}));

    return mapWithState;
  }

  removeLayerDocumentsForEvent(currentMapState: MapWithState, changeEvent: LayerDataChangeEvent): MapWithState {
    const { layerUId, changes } = changeEvent;

    const newState = this.removeLayerDocumentUIds(currentMapState, layerUId, Object.keys(changes));

    this.layerDataChangeEvents$.next(changeEvent);

    return newState;
  }

  removeLayerDocuments(currentMapState: MapWithState, layerUId: string): MapWithState {
    if (!!!currentMapState || !!!currentMapState?.layerStates || !!!currentMapState?.layerStates[layerUId]) {
      return currentMapState;
    }

    return this.removeLayerDocumentUIds(currentMapState, layerUId, Object.keys(currentMapState.layerStates[layerUId]?.documents));
  }

  private removeLayerDocumentUIds(currentMapState: MapWithState, layerUId: string, documentUIds: string[]): MapWithState {
    const mapWithState = produce(currentMapState, draft => {
      this.deleteMapStateLayerDocuments(draft, layerUId, documentUIds);
    });

    return mapWithState;
  }

  replaceLayerDocumentsForEvent(currentMapState: MapWithState, changeEvent: LayerDataChangeEvent): MapWithState {
    const { layerUId, changes, totalDocumentCount } = changeEvent;
    const layerState = currentMapState?.layerStates[layerUId];

    let replacedMapWithState = currentMapState;

    // if the layer state exists, create a remove event for all of the existing documents
    // this will result in the existing items being removed from the map
    if (!!layerState) {
      const documentsToRemove = { ...layerState.documents };
      if (Object.keys(documentsToRemove).length > 0) {
        const removeEvent = {
          type: LayerDataChangeType.Remove,
          layerUId,
          changes: documentsToRemove
        };

        replacedMapWithState = this.removeLayerDocumentsForEvent(currentMapState, removeEvent);
      }
    }

    // now create an "add" event which will add the items to the map
    const addEvent = {
      type: LayerDataChangeType.Add,
      layerUId,
      changes,
      totalDocumentCount
    };

    replacedMapWithState = this.addLayerDocumentsForEvent(replacedMapWithState, addEvent, true);

    return replacedMapWithState;
  }

  calculateDocumentCountsForLayerAndFolders(mapState: MapWithState, layerUId: string, totalDocumentCount?: number): void {
    const layerState = mapState.layerStates[layerUId];

    if (!layerState) {
      return;
    }

    layerState.documentCount = totalDocumentCount || (!!layerState?.documents ? Object.keys(layerState.documents)?.length : 0);

    const layer = mapState.layersSet[layerUId];

    if (!!!layer || layer.isActive !== true) {
      return;
    }

    let folder = mapState?.foldersSet[layer.folderUId];

    while (!!folder) {
      const layerCount = folder.layers?.reduce<number>((prevCount, layer) => {
        const folderLayerState = mapState.layerStates[layer.uId];
        return prevCount + (folderLayerState?.documentCount || 0);
      }, 0);

      const childFolderCount = folder.folders?.reduce<number>((prevCount, childFolder) => {
        const folderState = mapState.folderStates[childFolder.uId];
        return prevCount + (folderState?.documentCount || 0);
      }, 0);

      const currentFolderState = mapState.folderStates[folder.uId];
      currentFolderState.documentCount = layerCount + childFolderCount;

      folder = !!folder.parentUId ? mapState.foldersSet[folder.parentUId] : null;
    }
  }

  sendNotificationsForEvent(mapState: MapWithState, layerUId: string, documents: Document[]): void {
    const layer = mapState.layersSet[layerUId];
    if (!layer) {
      return;
    }

    if (!layer.displayOptions?.includeInNotifications) {
      return;
    }

    const dataset = mapState.datasetsSet[layer.datasetUId];
    if (!dataset) {
      return;
    }

    const userContext = this.userContextService.getUserContext();

    const soundUrl = layer.displayOptions?.notificationSound;

    for (let document of documents) {
      const location = this.dataFormattingService.getLocationForDisplay(layer, dataset, document);
      const translations: NotificationTranslation[] = [
        {
          cultureCode: userContext.cultureCode,
          subject: null,
          message: this.getMessageBodyFromDocument(document),
          location
        }
      ];

      let fromIcon = '';
      const providerKey = this.getProviderKey(document.provider?.toLowerCase());
      if (!!providerKey) {
        fromIcon = AlertProviderIcons[providerKey];
      }

      const layerName = layer.name;
      const categoryName = document.Category ?? document.category;
      const fromName = !!categoryName ? `${layerName} | ${categoryName}` : layerName;
      const geometry = this.getGeometryValue(layer, dataset, document);

      const notification: Notification = {
        uId: v4(),
        datasetUId: dataset.uId,
        documentUId: document.uid,
        fromIcon,
        fromName,
        distance: -1,
        geometry,
        dateSent: document.date_updated,
        messageType: NotificationType.Alert,
        translations,
        notificationStatus: NotificationStatus.Unread,
        isTransient: true,
        soundUrl
      };

      this.notificationService.addNotification(notification);
    }
  }

  getGeometryValue(layer: Layer, dataset: Dataset, document: Document) {
    let geoField = null;

    let geoFieldName = layer?.displayOptions?.geoFieldsToDisplay?.length > 0 ? layer?.displayOptions?.geoFieldsToDisplay[0] : null;
    if (!!geoFieldName) {
      geoField = dataset.schema.fields.find(f => f.systemName === geoFieldName);
    }

    // if we have not found the geo field then default to the first geometry in the schema
    if (!!!geoField) {
      geoField = dataset.schema.fields.find(f => f.dataType === FieldDataType.Geometry);
    }

    if (!!!geoField) {
      return '';
    }

    return document[geoField.systemName];
  }

  getProviderKey(provider): string {
    if (!!!provider) {
      return null;
    }

    return Object.keys(AlertProviderIcons).find(k => provider.startsWith(k));
  }

  getMessageBodyFromDocument(document: Document) {
    let message: string = document.name ?? document.summary ?? document.title ?? document.subject ?? document.description;

    if (!message) {
      message = '';
    }

    if (!!message && message.length > 150) {
      message = `${message.substring(0, 150)}...`;
    }

    return message;
  }
}
